<template>
  <dashboard-layout :dark="dark" :active="5">
    <div class="pt-4 pb-5">
      <div class="container-fluid">
        <div class="row">
          <div class="col-12">
            <p class="h5 text-primary fw-bold">
              Transactions
            </p>
          </div>
        </div>
      </div>
    </div>
    <div class="pb-5">
      <div class="container-fluid">
        <div class="row g-4 mb-4">
          <div class="col-12 col-md-4">
            <div class="card border-0 rounded-default box-shadow h-100">
              <div class="card-body">
                <p class="text-gray mb-2">Total Transactions</p>
                <h3 class="fw-semi-bold mb-0">{{ transactions.length }}</h3>
              </div>
            </div>
          </div>
          <div class="col-12 col-md-4">
            <div class="card border-0 rounded-default box-shadow h-100">
              <div class="card-body">
                <p class="text-gray mb-2">Deposits</p>
                <h3 class="fw-semi-bold mb-0">{{ depositCount }}</h3>
              </div>
            </div>
          </div>
          <div class="col-12 col-md-4">
            <div class="card border-0 rounded-default box-shadow h-100">
              <div class="card-body">
                <p class="text-gray mb-2">Withdrawals</p>
                <h3 class="fw-semi-bold mb-0">{{ withdrawalCount }}</h3>
              </div>
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-12">
            <div
              class="table-card border-0 rounded-default box-shadow transaction-table"
            >
              <div class="card-header bg-transparent border-0 p-0">
                <h2 class="h3 fw-semi-bold card-title mb-5">
                  Transaction List
                </h2>
              </div>
              <div class="card-body p-0">
                <table>
                  <thead>
                    <tr :class="headerBg">
                      <th>Date</th>
                      <th>Client</th>
                      <th>Country</th>
                      <th>Type</th>
                      <th>Method / Token</th>
                      <th>Wallet</th>
                      <th>Settled Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(transaction, index) in paginatedTransactions"
                      :key="`${transaction.wallet}-${transaction.date}-${index}`"
                      :class="rowBg"
                    >
                      <td data-label="Date">
                        <span class="fw-semi-bold">{{ formatDate(transaction.date) }}</span>
                      </td>
                      <td data-label="Client">
                        <span class="fw-semi-bold">{{ transaction.clientName }}</span>
                      </td>
                      <td data-label="Country">
                        <span class="text-gray">{{ transaction.country }}</span>
                      </td>
                      <td data-label="Type">
                        <span
                          class="badge inline-flex align-items-center text-white"
                          :class="
                            transaction.type === 'Deposit'
                              ? 'bg-success'
                              : 'bg-danger'
                          "
                          >{{ transaction.type }}</span
                        >
                      </td>
                      <td data-label="Method / Token">
                        <span class="fw-semi-bold">{{ transaction.methodToken }}</span>
                      </td>
                      <td data-label="Wallet">
                        <span class="text-gray">{{ formatWallet(transaction.wallet) }}</span>
                      </td>
                      <td data-label="Settled Amount">
                        <span
                          class="fw-semi-bold"
                          :class="
                            transaction.type === 'Deposit'
                              ? 'text-success'
                              : 'text-danger'
                          "
                        >
                          {{ transaction.type === "Deposit" ? "+" : "-" }}{{ formatAmount(transaction.settledAmount) }}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
                <div class="d-flex justify-content-between align-items-center flex-column flex-md-row gap-3 mt-4">
                  <p class="text-gray mb-0">
                    Showing {{ pageStart }}-{{ pageEnd }} of {{ transactions.length }}
                    transactions
                  </p>
                  <b-pagination
                    v-model="currentPage"
                    :total-rows="transactions.length"
                    :per-page="perPage"
                    pills
                    first-number
                    last-number
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </dashboard-layout>
</template>

<script>
import DashboardLayout from "../../components/Dashboard/DashboardLayout.vue";
import transactions from "../../data/transactions";

export default {
  components: { DashboardLayout },
  props: { dark: Boolean },
  data() {
    return {
      currentPage: 1,
      perPage: 25,
      transactions,
    };
  },
  computed: {
    rowBg() {
      return this.dark ? "bg-dark-2" : "";
    },
    headerBg() {
      return this.dark ? "bg-dark-2 text-white" : "bg-light-200";
    },
    depositCount() {
      return this.transactions.filter(
        (transaction) => transaction.type === "Deposit"
      ).length;
    },
    withdrawalCount() {
      return this.transactions.filter(
        (transaction) => transaction.type === "Withdrawal"
      ).length;
    },
    paginatedTransactions() {
      const start = (this.currentPage - 1) * this.perPage;
      return this.transactions.slice(start, start + this.perPage);
    },
    pageStart() {
      if (!this.transactions.length) {
        return 0;
      }

      return (this.currentPage - 1) * this.perPage + 1;
    },
    pageEnd() {
      return Math.min(this.currentPage * this.perPage, this.transactions.length);
    },
  },
  methods: {
    formatAmount(value) {
      return new Intl.NumberFormat("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);
    },
    formatDate(value) {
      const parsedDate = this.parseDate(value);

      if (!parsedDate) {
        return value;
      }

      return new Intl.DateTimeFormat("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }).format(parsedDate);
    },
    formatWallet(wallet) {
      if (!wallet || wallet.length <= 14) {
        return wallet;
      }

      return `${wallet.slice(0, 8)}...${wallet.slice(-6)}`;
    },
    parseDate(value) {
      if (!value) {
        return null;
      }

      if (typeof value === "string") {
        const slashDateMatch = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);

        if (slashDateMatch) {
          const [, day, month, year] = slashDateMatch;
          const parsedSlashDate = new Date(
            Number(year),
            Number(month) - 1,
            Number(day)
          );

          return Number.isNaN(parsedSlashDate.getTime()) ? null : parsedSlashDate;
        }
      }

      const parsedDate = new Date(value);
      return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
    },
  },
};
</script>
